import React, { useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import axios from 'axios';
import { InputGroup } from 'react-bootstrap';
import InputMask from 'react-input-mask';
import {useEffect, useRef } from 'react';

import injectCollectJS from '../injectCollect';
import { useLanguage } from '../contexts/LanguageContext';


// All of the top-up behaviour lives here — amounts, the credit ladder, card
// fields and the CollectJS wallet init. It is deliberately NOT a modal, so it
// can be dropped inside one that already exists (the boost confirm step) as
// well as being shown on its own via <Balance /> below.
//
// Only ever render ONE of these at a time. CollectJS binds Apple/Google Pay to
// the element ids #googlepaybutton / #applepaybutton, so a second copy would
// re-init against the same ids and leave the first one's buttons dead.
// Pay the left figure, receive the right in balance. The server resolves the
// credit from its own copy of this ladder (routes/purchase.js TOPUP_TIERS) —
// keep the two in step. Amounts off the ladder credit 1:1, so they show no
// bonus. Exported so a caller can quote the bonus without a second copy.
export const balancePackages = [
  { pay: 25, credit: 33 },
  { pay: 50, credit: 68 },
  { pay: 100, credit: 140 },
  { pay: 250, credit: 365 },
];

// The tier a customer short by `total` would most likely reach for: the
// cheapest one that covers it, or the largest if nothing does.
export const closestTier = (total) =>
  balancePackages.find((t) => t.pay >= (Number(total) || 0)) || balancePackages[balancePackages.length - 1];

// fixedAmount charges exactly that and hides the ladder — it is the "pay what
// this boost costs" path, where the figure is the boost's price rather than
// something the customer picks. It credits 1:1, so there is no bonus to show.
export const BalanceTopUp = ({ onHide, profile, onSuccess, inline = false, fixedAmount = null, directPack = false }) => {
  const L = useLanguage();

  const [selectedAmount, setSelectedAmount] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardTouched, setCardTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const cardRef = useRef(null);
  const expRef = useRef(null);
  const cvvRef = useRef(null);
  const [customAmount, setCustomAmount] = useState('');
  const mountedRef = useRef(true);
  const [isReinitializingCollect, setIsReinitializingCollect] = useState(false);
  const [functionError, setFunctionError] = useState('');

  const creditFor = (pay) => balancePackages.find((t) => t.pay === pay)?.credit ?? pay;
  const MIN_TOPUP = 10;

  // Preselect the card already linked to the active profile, falling back to
  // the only card on file. Leaving it unselected made the customer re-pick a
  // card they had already given us before Pay would do anything.
  // Being mounted is now the "shown" condition: the modal shell unmounts this
  // when it closes, and inline it only renders when the balance is short.
  useEffect(() => {
    if (cardTouched) return;
    const cards = profile?.cards || {};
    const ids = Object.keys(cards);
    if (ids.length === 0) return;
    const linked = profile?.socials?.[profile?.selectedProfile]?.cardid;
    const preferred = (linked && cards[linked]) ? String(linked) : (ids.length === 1 ? ids[0] : null);
    if (preferred) setSelectedCard(preferred);
  }, [profile, cardTouched]);

  const handlePurchase = async (e) => {
	e.preventDefault();
    const amount = fixedAmount != null ? fixedAmount : (selectedAmount === 'custom' ? parseInt(customAmount) : selectedAmount);
    
    if (!amount || !selectedCard) {
      setFunctionError(L.balance.errSelectBoth);
      return;
    }

    // Mirrors the server's floor so a custom amount fails here, not at NMI.
    if (fixedAmount == null && amount < MIN_TOPUP) {
      setFunctionError(L.balance.minTopup(MIN_TOPUP));
      return;
    }

    setFunctionError('');
    setLoading(true);
    try {
      let data = {};
      if (selectedCard === 'addCard') {
        const cardNumber = cardRef.current.value.replace(/\s/g, '');
        const expDate = expRef.current.value;
        const cvv = cvvRef.current.value;

        if (!cardNumber || !expDate || !cvv) {
          throw new Error(L.balance.errFillCardDetails);
        }

        data = {
          amount: amount,
        exact: fixedAmount != null,
			directPack,
          card: cardNumber,
          exp: expDate,
          cvv: cvv
        };
      } else {
        data = {
          amount: amount,
        exact: fixedAmount != null,
			directPack,
          cardid: selectedCard
        };
      }

      const response = await axios.post('https://api.viralgpt.com/purchase/balance', data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (response.data.success) {
        onHide();
        // A caller that wants to carry on (e.g. finish the boost the customer
        // was blocked from) handles it; otherwise reload as before.
        if (onSuccess) onSuccess(creditFor(Number(amount)));
        else window.location.reload();
      } else {
        throw new Error(response.data.message || L.balance.errPaymentFailed);
      }
    } catch (err) {
      setFunctionError(err.response?.data?.error || err.message || L.balance.errPaymentFailedTryAgain);
    } finally {
      setLoading(false);
    }
  };



  const purchaseApplePay = async (paymenttoken, cardDetails) => {
    const amount = fixedAmount != null ? fixedAmount : (selectedAmount === 'custom' ? parseInt(customAmount) : selectedAmount);
    
    if (!amount) {
      setFunctionError(L.balance.errSelectAmount);
      return;
    }

    setFunctionError('');
    setLoading(true);
    try {
      const data = {
        amount: amount,
        exact: fixedAmount != null,
		directPack,
        paymenttoken: paymenttoken
      };

      const response = await axios.post('https://api.viralgpt.com/purchase/balance', data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (response.data.success) {
        onHide();
        if (onSuccess) onSuccess(creditFor(Number(amount)));
        else window.location.reload();
      } else {
        throw new Error(response.data.message || L.balance.errPaymentFailed);
      }
    } catch (err) {
      setFunctionError(err.response?.data?.error || L.balance.errPaymentFailedTryAgain);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
	var price = fixedAmount != null ? Number(fixedAmount) : parseFloat(selectedAmount === 'custom' ? customAmount : selectedAmount);

	// CollectJS bakes the amount into the wallet sheet at init, so there is
	// nothing to mount until one is chosen. It was being handed "NaN", which
	// failed silently and left Apple/Google Pay missing entirely.
	if (!Number.isFinite(price) || price <= 0) {
		setIsReinitializingCollect(false);
		return;
	}

	setIsReinitializingCollect(true);
	
	const timeoutId = setTimeout(() => {
		if (!mountedRef.current) return;
		
		try {
			injectCollectJS('https://secure.nmi.com/token/Collect.js', '9t4q59-U47k7W-Cj7Ty9-MbC6Y6', purchaseApplePay, price.toString())
				.then(() => {
					if (!mountedRef.current) return;
					setIsReinitializingCollect(false);
				})
				.catch((error) => {
					if (!mountedRef.current) return;
					console.error('Error initializing CollectJS:', error);
					setIsReinitializingCollect(false);
				});
		} catch (error) {
			if (!mountedRef.current) return;
			console.error('Error calling injectCollectJS:', error);
			setIsReinitializingCollect(false);
		}
	}, 300);

	return () => {
		clearTimeout(timeoutId);
	};

  }, [selectedAmount, customAmount]);


  const displayAmount = fixedAmount != null ? fixedAmount : (selectedAmount === 'custom' ? customAmount || 0 : selectedAmount || 0);
  const hasPayableAmount = fixedAmount != null || !!selectedAmount;
  const displayCredit = creditFor(Number(displayAmount));

  return (
    <>
      {/* Custom styled header. Inline, the surrounding modal supplies its own. */}
      {!inline && (
      <div style={{padding: '1.5rem 1.5rem 0'}}>
        <div className="flex items-center justify-between">
          <div>
            <h5 style={{fontWeight: 700, color: '#0a2540', fontSize: '1.15rem', marginBottom: '2px'}}>{L.balance.title}</h5>
            <p style={{color: '#68778d', fontSize: '0.82rem', margin: 0}}>{L.balance.subtitle}</p>
          </div>
          <button onClick={onHide} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
            color: '#68778d', fontSize: '1.2rem', lineHeight: 1
          }}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
      )}

      <Modal.Body style={{padding: inline ? '0' : '1.25rem 1.5rem 1.5rem'}}>

        {/* Error banner */}
        {functionError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
            padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <i className="fas fa-exclamation-circle" style={{color: '#dc2626', fontSize: '0.85rem'}}></i>
            <span style={{color: '#991b1b', fontSize: '0.82rem', fontWeight: 500}}>{functionError}</span>
          </div>
        )}

        {fixedAmount == null && (
        <>
        {/* Amount selection */}
        <div style={{marginBottom: '20px'}}>
          <label style={{fontSize: '0.75rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>
            {L.balance.selectAmount}
          </label>
          {/* Four presets on an even 2×2 (or 4-across) grid. The custom option
              used to be a fifth tile in a 3-column grid, which left it orphaned
              on its own row and reading like a preset. It is now a secondary
              choice below the grid, which is also the hierarchy we want: presets
              carry a bonus, custom does not. */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px'}}>
            {balancePackages.map(({ pay, credit }) => (
              <div
                key={pay}
                onClick={() => { setSelectedAmount(pay); setFunctionError(''); }}
                style={{
                  padding: '16px 8px', textAlign: 'center', borderRadius: '12px', cursor: 'pointer',
                  border: selectedAmount === pay ? '2px solid #635bff' : '1.5px solid #e3e8ee',
                  background: selectedAmount === pay ? '#f5f3ff' : '#fff',
                  boxShadow: selectedAmount === pay ? '0 0 0 3px rgba(99,91,255,0.1)' : 'none',
                  transition: 'all 0.15s ease',
                }}
              >
                <span style={{fontSize: '1.3rem', fontWeight: 800, color: selectedAmount === pay ? '#635bff' : '#0a2540', display: 'block', lineHeight: 1.15}}>
                  ${pay}
                </span>
                {/* The bonus is the reason to pick a preset over the custom field. */}
                <span style={{fontSize: '0.75rem', fontWeight: 700, color: '#1a7f4b', display: 'block', marginTop: 3}}>
                  {L.balance.bonusAmount(credit - pay)}
                </span>
              </div>
            ))}
          </div>

          <div
            onClick={() => { setSelectedAmount('custom'); setFunctionError(''); }}
            style={{
              marginTop: '10px', padding: '11px 14px', borderRadius: '10px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              border: selectedAmount === 'custom' ? '2px solid #635bff' : '1.5px solid #e3e8ee',
              background: selectedAmount === 'custom' ? '#f5f3ff' : '#fff',
              transition: 'all 0.15s ease',
            }}
          >
            <span style={{fontSize: '0.85rem', fontWeight: 600, color: selectedAmount === 'custom' ? '#635bff' : '#0a2540'}}>
              {L.balance.custom}
            </span>
            <span style={{fontSize: '0.72rem', color: '#8898aa'}}>{L.balance.customNoBonus}</span>
          </div>
        </div>

        {/* Custom amount input */}
        {selectedAmount === 'custom' && (
          <div style={{marginBottom: '20px'}}>
            <div style={{position: 'relative'}}>
              <span style={{position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#68778d', fontWeight: 600, fontSize: '0.9rem'}}>$</span>
              <input
                type="number"
                min="10"
                max="1000"
                placeholder={L.balance.enterAmountPlaceholder}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px 10px 30px', borderRadius: '8px',
                  border: '1.5px solid #e3e8ee', fontSize: '0.9rem', fontWeight: 500,
                  color: '#0a2540', outline: 'none', transition: 'border-color 0.15s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#635bff'}
                onBlur={(e) => e.target.style.borderColor = '#e3e8ee'}
              />
            </div>
          </div>
        )}

        {/* Order summary */}
        {(selectedAmount || customAmount) && (
          <div style={{
            background: '#f6f9fc', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px',
            border: '1px solid #e3e8ee'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <span style={{fontSize: '0.82rem', color: '#68778d', fontWeight: 500}}>{L.balance.total}</span>
              <span style={{fontSize: '1.35rem', fontWeight: 700, color: '#0a2540'}}>${displayAmount}</span>
            </div>
            {/* Only a ladder amount earns a bonus, so this row appears only when
                the credit differs from what's being charged. */}
            {displayCredit > Number(displayAmount) && (
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 6, borderTop: '1px solid #e3e8ee'}}>
                <span style={{fontSize: '0.82rem', color: '#68778d', fontWeight: 500}}>{L.balance.youReceive}</span>
                <span style={{fontSize: '1rem', fontWeight: 700, color: '#1a7f4b'}}>${displayCredit}</span>
              </div>
            )}
          </div>
        )}

        </>
        )}

        {/* Payment method */}
        <Form onSubmit={handlePurchase}>
          <div style={{marginBottom: '16px'}}>
            <label style={{fontSize: '0.75rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block'}}>
              {L.balance.paymentMethod}
            </label>
            <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
              {Object.entries(profile.cards).map(([id, card]) => (
                <div
                  key={id}
                  onClick={() => { setCardTouched(true); setSelectedCard(id); setFunctionError(''); }}
                  style={{
                    padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                    border: selectedCard === id ? '2px solid #635bff' : '1.5px solid #e3e8ee',
                    background: selectedCard === id ? '#f5f3ff' : '#fff',
                    transition: 'all 0.15s ease',
                    display: 'flex', alignItems: 'center', gap: '10px',
                  }}
                >
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: selectedCard === id ? '#ede9fe' : '#f1f5f9',
                  }}>
                    {card.cardnumber.startsWith('4') && <i className="fa-brands fa-cc-visa" style={{color: '#635bff', fontSize: '1rem'}}></i>}
                    {card.cardnumber.startsWith('5') && <i className="fa-brands fa-cc-mastercard" style={{color: '#635bff', fontSize: '1rem'}}></i>}
                    {card.cardnumber.startsWith('3') && <i className="fa-brands fa-cc-amex" style={{color: '#635bff', fontSize: '1rem'}}></i>}
                    {!card.cardnumber.startsWith('3') && !card.cardnumber.startsWith('4') && !card.cardnumber.startsWith('5') && <i className="fa-solid fa-credit-card" style={{color: '#635bff', fontSize: '0.85rem'}}></i>}
                  </div>
                  <span style={{fontWeight: 600, fontSize: '0.85rem', color: '#0a2540'}}>
                    •••• {card.cardnumber.slice(-4)}
                  </span>
                  <span style={{fontSize: '0.78rem', color: '#68778d'}}>{card.exp}</span>
                  {selectedCard === id && (
                    <i className="fas fa-check-circle" style={{marginLeft: 'auto', color: '#635bff', fontSize: '0.9rem'}}></i>
                  )}
                </div>
              ))}

              {/* Add new card option */}
              <div
                onClick={() => { setCardTouched(true); setSelectedCard('addCard'); setFunctionError(''); }}
                style={{
                  padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                  border: selectedCard === 'addCard' ? '2px solid #635bff' : '1.5px dashed #d1d5db',
                  background: selectedCard === 'addCard' ? '#f5f3ff' : '#fff',
                  transition: 'all 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}
              >
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: selectedCard === 'addCard' ? '#ede9fe' : '#f1f5f9',
                }}>
                  <i className="fas fa-plus" style={{color: '#635bff', fontSize: '0.75rem'}}></i>
                </div>
                <span style={{fontWeight: 600, fontSize: '0.85rem', color: '#0a2540'}}>{L.balance.addNewCard}</span>
                {selectedCard === 'addCard' && (
                  <i className="fas fa-check-circle" style={{marginLeft: 'auto', color: '#635bff', fontSize: '0.9rem'}}></i>
                )}
              </div>
            </div>
          </div>

          {/* New card form */}
          {(profile.cards.length == 0 || selectedCard == 'addCard') && (
            <div style={{
              background: '#f6f9fc', borderRadius: '10px', padding: '16px', marginBottom: '16px',
              border: '1px solid #e3e8ee'
            }}>
              <div style={{marginBottom: '10px'}}>
                <label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px', display: 'block'}}>{L.balance.cardNumber}</label>
                <div style={{position: 'relative'}}>
                  <i className="fas fa-credit-card" style={{position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem'}}></i>
                  <Form.Control
                    as={InputMask}
                    type="text"
                    ref={cardRef}
                    placeholder="1234 5678 9012 3456"
                    mask="9999 9999 9999 9999"
                    maskChar=""
                    required
                    style={{
                      paddingLeft: '36px', borderRadius: '8px', border: '1.5px solid #e3e8ee',
                      fontSize: '0.88rem', fontWeight: 500, padding: '10px 12px 10px 36px',
                    }}
                  />
                </div>
              </div>
              <div style={{display: 'flex', gap: '10px'}}>
                <div style={{flex: 1}}>
                  <label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px', display: 'block'}}>{L.balance.expiry}</label>
                  <div style={{position: 'relative'}}>
                    <i className="fas fa-calendar" style={{position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem'}}></i>
                    <Form.Control
                      as={InputMask}
                      maskChar=""
                      type="text"
                      ref={expRef}
                      placeholder="MM/YY"
                      mask="99/99"
                      required
                      style={{
                        paddingLeft: '36px', borderRadius: '8px', border: '1.5px solid #e3e8ee',
                        fontSize: '0.88rem', fontWeight: 500, padding: '10px 12px 10px 36px',
                      }}
                    />
                  </div>
                </div>
                <div style={{flex: 1}}>
                  <label style={{fontSize: '0.72rem', fontWeight: 600, color: '#68778d', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px', display: 'block'}}>{L.balance.cvv}</label>
                  <div style={{position: 'relative'}}>
                    <i className="fas fa-lock" style={{position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem'}}></i>
                    <Form.Control
                      as={InputMask}
                      maskChar=""
                      type="text"
                      ref={cvvRef}
                      placeholder="123"
                      mask="9999"
                      required
                      style={{
                        paddingLeft: '36px', borderRadius: '8px', border: '1.5px solid #e3e8ee',
                        fontSize: '0.88rem', fontWeight: 500, padding: '10px 12px 10px 36px',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pay button */}
          <button
            type="submit"
            disabled={loading || !hasPayableAmount || !selectedCard}
            style={{
              width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
              fontWeight: 700, fontSize: '0.9rem', cursor: loading || !hasPayableAmount || !selectedCard ? 'not-allowed' : 'pointer',
              color: '#fff', transition: 'all 0.15s ease',
              background: loading || !hasPayableAmount || !selectedCard ? '#c4b5fd' : 'linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)',
              boxShadow: loading || !hasPayableAmount || !selectedCard ? 'none' : '0 2px 8px rgba(99,91,255,0.3)',
              opacity: loading || !hasPayableAmount || !selectedCard ? 0.7 : 1,
            }}
          >
            {loading ? (
              <><i className="fas fa-spinner fa-spin" style={{marginRight: '6px'}}></i> {L.balance.processing}</>
            ) : (
              <>{L.balance.pay(displayAmount)}</>
            )}
          </button>

          {/* Wallet buttons. CollectJS mounts into these ids and bakes the
              amount in at init, so the effect above re-runs when it changes. */}
          <div style={{marginTop: '12px'}} id="googlepaybutton"></div>
          {window.location.hostname !== 'localhost' && (
            <div style={{marginTop: '8px'}} id="applepaybutton"></div>
          )}

          {/* Trust badges */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px', paddingTop: '12px',
            borderTop: '1px solid #e3e8ee'
          }}>
            <span style={{fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px'}}>
              <i className="fas fa-lock" style={{fontSize: '0.6rem'}}></i> {L.balance.secure}
            </span>
            <span style={{fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px'}}>
              <i className="fas fa-bolt" style={{fontSize: '0.6rem'}}></i> {L.balance.instant}
            </span>
            <span style={{fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px'}}>
              <i className="fas fa-shield-halved" style={{fontSize: '0.6rem'}}></i> {L.balance.encrypted}
            </span>
          </div>
        </Form>
      </Modal.Body>
    </>
  );
};

// The standalone top-up dialog, unchanged for every existing caller.
const Balance = ({ show, onHide, profile, onSuccess }) => (
  <Modal show={show} onHide={onHide} size="md" centered>
    <BalanceTopUp onHide={onHide} profile={profile} onSuccess={onSuccess} />
  </Modal>
);

export default Balance;
